import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { Dropdown } from "primereact/dropdown";
import MeaPage from "../../../components/MeaPage";
import SecretariatWorkspaceBar from "../components/SecretariatWorkspaceBar";
import MeaButton from "../../../components/MeaButton";
import classesAPI from "../../../services/classes";
import trainingsAPI from "../../../services/trainings";
import slotsAPI from "../../../services/slots";
import { PERMISSION_TRAINING_CLASSES } from "../../../permissions";

const SecretariatClassesDetailPage = () => {
  const { schoolId, trainingId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Slot filter state - initialize from URL params
  const [selectedSlotId, setSelectedSlotId] = useState(searchParams.get('slot_id') || "");

  // Update URL when slot filter changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (selectedSlotId) {
      newSearchParams.set('slot_id', selectedSlotId);
    } else {
      newSearchParams.delete('slot_id');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [selectedSlotId, searchParams, setSearchParams]);

  // Handle slot filter change
  const handleSlotChange = (e) => {
    setSelectedSlotId(e.value);
  };

  const handlePrintAllClasses = () => {
    const url = selectedSlotId 
      ? `/school/${schoolId}/secretariat/classes/${trainingId}/print-all-classes?slot_id=${selectedSlotId}`
      : `/school/${schoolId}/secretariat/classes/${trainingId}/print-all-classes`;
    navigate(url);
  };

  // Get slots for filtering
  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    ""
  );

  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    { slot_id: selectedSlotId }
  );

  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, accessToken);
  const training = trainingQuery?.data?.data;

  const renderClassCard = (classe) => (
    <div key={classe.id} className="col-12 md:col-6 xl:col-4 p-3">
      <Card
        className="h-full cursor-pointer hover:shadow-3 transition-all transition-duration-200"
        onClick={() => navigate(`/school/${schoolId}/secretariat/classes/${trainingId}/classe/${classe.id}`)}
      >
        <div className="flex flex-column h-full">
          <div className="flex align-items-center mb-3">
            <img 
              src="/images/icons/trainings.png" 
              style={{ width: '50px', height: '50px' }} 
              className="mr-3" 
              alt="classe" 
            />
            <div className="flex flex-column">
              <h5 className="text-900 font-medium mb-1">{classe.name}</h5>
              {classe.level && (
                <span className="text-600 text-sm">{classe.level.name}</span>
              )}
            </div>
          </div>
          
          <div className="flex-1">
            {classe.presentation && (
              <p className="text-600 text-sm mb-2 line-height-3">
                {classe.presentation.length > 100 
                  ? classe.presentation.substring(0, 100) + "..." 
                  : classe.presentation
                }
              </p>
            )}
            
            <div className="flex justify-content-between align-items-center text-sm">
              <span className="text-600">
                <i className="pi pi-users mr-1"></i>
                {classe.total_students || 0} élève{classe.total_students > 1 ? 's' : ''}
              </span>
              {classe.max_capacity && (
                <span className="text-600">
                  <i className="pi pi-building mr-1"></i>
                  Capacité: {classe.max_capacity}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderSkeleton = () => (
    <div className="col-12 md:col-6 xl:col-4 p-3">
      <Card>
        <div className="flex align-items-center mb-3">
          <Skeleton width="50px" height="50px" className="mr-3" />
          <div className="flex flex-column flex-1">
            <Skeleton width="80%" height="1.2rem" className="mb-2" />
            <Skeleton width="60%" height="1rem" />
          </div>
        </div>
        <Skeleton width="100%" height="3rem" className="mb-2" />
        <div className="flex justify-content-between">
          <Skeleton width="40%" height="1rem" />
          <Skeleton width="40%" height="1rem" />
        </div>
      </Card>
    </div>
  );

  // Slot filter options
  const slotOptions = useMemo(() => {
    const slots = slotsQuery?.data?.data || [];
    return [
      { label: "Tous les créneaux", value: "" },
      ...slots.map(slot => ({
        label: `${slot.name} (${slot.start_hour} - ${slot.end_hour})`,
        value: slot.id
      }))
    ];
  }, [slotsQuery?.data?.data]);

  const customActions = (
    <div className="flex gap-2">
      <Button
        label="Imprimer"
        icon="pi pi-print"
        className="mr-2"
        onClick={handlePrintAllClasses}
      />
    </div>
  );

  return (
    <MeaPage      
      title={`Classes de la formation ${training?.name || ''}`}
      breadcrumb={<SecretariatWorkspaceBar section="classes" training={training} />}
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${schoolId}/secretariat`)}
      customActions={customActions}
    >
      {/* Slot Filter */}
      <div className="mb-4">
        <div className="flex align-items-center gap-3">
          <label htmlFor="slotFilter" className="font-medium text-900">
            Filtrer par créneau :
          </label>
          <Dropdown
            id="slotFilter"
            value={selectedSlotId}
            options={slotOptions}
            onChange={handleSlotChange}
            placeholder={slotsQuery.isLoading ? "Chargement..." : "Sélectionner un créneau"}
            className="w-full md:w-20rem"
            showClear
            disabled={slotsQuery.isLoading}
            loading={slotsQuery.isLoading}
          />
        </div>
        {slotsQuery.isError && (
          <div className="text-red-500 text-sm mt-2">
            Erreur lors du chargement des créneaux
          </div>
        )}
      </div>

      <div className="grid grid-nogutter">
        {classesQuery.isLoading ? (
          // Show skeleton loading cards
          Array.from({ length: 6 }).map((_, index) => (
            <React.Fragment key={index}>
              {renderSkeleton()}
            </React.Fragment>
          ))
        ) : classesQuery.isError ? (
          <div className="col-12">
            <Card>
              <div className="text-center p-4">
                <i className="pi pi-exclamation-triangle text-4xl text-orange-500 mb-3"></i>
                <h4 className="text-900 mb-2">Erreur de chargement</h4>
                <p className="text-600 mb-3">Impossible de charger la liste des classes.</p>
                <Button
                  label="Réessayer"
                  icon="pi pi-refresh"
                  onClick={() => classesQuery.refetch()}
                />
              </div>
            </Card>
          </div>
        ) : classesQuery.data?.data?.length > 0 ? (
          classesQuery.data.data.map(renderClassCard)
        ) : (
          <div className="col-12">
            <Card>
              <div className="text-center p-4">
                <i className="pi pi-inbox text-4xl text-400 mb-3"></i>
                <h4 className="text-900 mb-2">Aucune classe trouvée</h4>
                <p className="text-600">Il n'y a actuellement aucune classe dans cette formation.</p>
              </div>
            </Card>
          </div>
        )}
      </div>
    </MeaPage>
  );
};

export default SecretariatClassesDetailPage;
